import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Chargement, EnTetePage, Etiquette, Indicateur, Message, Vide } from "@cabinet/charte";
import {
  banqueApi,
  dossiersApi,
  ecrituresApi,
  ErreurApi,
  importApi,
  type ApercuVue,
  type CompteBancaireVue,
  type OperationVue,
  type SaisieDeRegle,
  type StatutDOperation,
} from "../api/client";
import { FicheDePiece } from "../composants/Piece";
import { Tiroir } from "../composants/Tiroir";
import {
  affectationDuReleve,
  colonnesDuReleve,
  manquesDuReleve,
  proposerAffectationDeReleve,
  type AffectationDeReleve,
  type ChampDeReleve,
} from "../domaine/banque";
import { lettreDeColonne } from "../domaine/importation";
import { formaterDate, formaterMontant, formaterNombre } from "../domaine/formats";
import { useSession } from "../session";

type Onglet = "operations" | "regles" | "rapprochement";

const aujourdHui = () => new Date().toISOString().slice(0, 10);

const message = (echec: unknown) => (echec instanceof ErreurApi ? echec.message : "L'opération n'a pas abouti.");

/**
 * Banque et trésorerie (M07).
 *
 * Un compte bancaire du dossier, son relevé importé, et chaque opération
 * traitée : pointée contre une écriture déjà passée, comptabilisée en
 * règlement d'une facture ouverte ou selon une règle, ou ignorée avec son
 * motif. L'écart entre le solde du relevé et le solde comptable reste affiché
 * en tête (EF-0706), et l'état de rapprochement dit ce qui l'explique.
 */
export function PageBanque() {
  const { id = "" } = useParams();
  const dossier = useQuery({ queryKey: ["dossier", id], queryFn: () => dossiersApi.consulter(id) });
  const comptes = useQuery({ queryKey: ["comptes-bancaires", id], queryFn: () => banqueApi.comptes(id) });
  const [choisi, setChoisi] = useState<string | null>(null);
  const [onglet, setOnglet] = useState<Onglet>("operations");
  const [ouverture, setOuverture] = useState(false);

  const compte = comptes.data?.find((c) => c.id === choisi) ?? comptes.data?.[0];
  const erreur = dossier.error ?? comptes.error;

  return (
    <>
      <EnTetePage
        surTitre={dossier.data?.raisonSociale ?? "Dossier"}
        titre="Banque"
        description="Le relevé s'importe et se dédoublonne ; chaque opération se pointe contre une écriture déjà passée, se comptabilise — en règlement d'une facture ouverte, lettrée aussitôt, ou selon une règle — ou s'ignore avec son motif."
        actions={
          <button type="button" className={ouverture ? undefined : "tertiaire"} onClick={() => setOuverture(!ouverture)}>
            {ouverture ? "Annuler" : "Ouvrir un compte bancaire"}
          </button>
        }
      />

      {erreur != null && <Message type="erreur">{erreur.message}</Message>}
      {comptes.isPending && <Chargement quoi="les comptes bancaires" />}

      {(ouverture || comptes.data?.length === 0) && (
        <OuvertureDeCompte
          dossier={id}
          surOuverture={(ouvert) => {
            setChoisi(ouvert.id);
            setOuverture(false);
          }}
        />
      )}

      {compte != null && (
        <>
          <div className="filtres-plan">
            <select value={compte.id} onChange={(e) => setChoisi(e.target.value)} aria-label="Compte bancaire">
              {comptes.data!.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.compte} — {c.libelle}
                </option>
              ))}
            </select>
            <div className="onglets">
              {(
                [
                  ["operations", "Opérations"],
                  ["regles", "Règles d'affectation"],
                  ["rapprochement", "État de rapprochement"],
                ] as const
              ).map(([valeur, libelle]) => (
                <button
                  key={valeur}
                  type="button"
                  className={onglet === valeur ? "principal" : "tertiaire"}
                  onClick={() => setOnglet(valeur)}
                >
                  {libelle}
                </button>
              ))}
            </div>
          </div>

          <BandeauDEcart compte={compte} />

          {onglet === "operations" && <Operations dossier={id} compte={compte} />}
          {onglet === "regles" && <Regles dossier={id} />}
          {onglet === "rapprochement" && <Rapprochement dossier={id} compte={compte} />}
        </>
      )}
    </>
  );
}

/* ------------------------------------------------------------------------ */

function OuvertureDeCompte({ dossier, surOuverture }: { dossier: string; surOuverture: (compte: CompteBancaireVue) => void }) {
  const cache = useQueryClient();
  const journaux = useQuery({ queryKey: ["journaux", dossier], queryFn: () => ecrituresApi.journaux(dossier) });
  const [saisie, setSaisie] = useState({
    compte: "514",
    journal: "BQ",
    libelle: "",
    banque: "",
    iban: "",
    soldeInitial: "0",
    dateDuSoldeInitial: `${new Date().getFullYear() - 1}-12-31`,
  });
  const ouverture = useMutation({
    mutationFn: () =>
      banqueApi.ouvrir(dossier, {
        ...saisie,
        banque: saisie.banque || null,
        iban: saisie.iban || null,
        soldeInitial: Number(saisie.soldeInitial.replace(/\s/g, "").replace(",", ".")),
      }),
    onSuccess: (ouvert) => {
      void cache.invalidateQueries({ queryKey: ["comptes-bancaires", dossier] });
      surOuverture(ouvert);
    },
  });
  const champ = (nom: keyof typeof saisie, libelle: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <label className="champ">
      {libelle}
      <input value={saisie[nom]} onChange={(e) => setSaisie({ ...saisie, [nom]: e.target.value })} {...props} />
    </label>
  );

  return (
    <form
      className="carte"
      onSubmit={(e) => {
        e.preventDefault();
        ouverture.mutate();
      }}
    >
      <h2>Ouvrir un compte bancaire</h2>
      {ouverture.error != null && <Message type="erreur">{message(ouverture.error)}</Message>}
      <div className="champs-en-ligne">
        {champ("compte", "Compte comptable", { size: 8, required: true })}
        <label className="champ">
          Journal
          <select value={saisie.journal} onChange={(e) => setSaisie({ ...saisie, journal: e.target.value })}>
            {(journaux.data ?? [])
              .filter((j) => j.type === "TRESORERIE")
              .map((j) => (
                <option key={j.code} value={j.code}>
                  {j.code} — {j.libelle}
                </option>
              ))}
          </select>
        </label>
        {champ("libelle", "Libellé", { placeholder: "Banque Populaire — compte courant", required: true })}
        {champ("banque", "Banque")}
        {champ("iban", "IBAN")}
        {champ("soldeInitial", "Solde du relevé", { size: 12, inputMode: "decimal" })}
        {champ("dateDuSoldeInitial", "au", { type: "date" })}
        <button type="submit" className="principal" disabled={ouverture.isPending}>
          Ouvrir
        </button>
      </div>
      <p className="discret-texte">
        Le solde du relevé à cette date est le point de départ du rapprochement : c'est lui que les à-nouveaux du compte
        doivent retrouver.
      </p>
    </form>
  );
}

/* ------------------------------------------------------------------------ */

/** L'écart entre relevé et comptabilité, affiché en permanence (EF-0706). */
function BandeauDEcart({ compte }: { compte: CompteBancaireVue }) {
  const etat = useQuery({
    queryKey: ["rapprochement", compte.id, "bandeau"],
    queryFn: () => banqueApi.rapprochement(compte.id, aujourdHui()),
    retry: false,
  });
  if (etat.data == null) return null;
  const e = etat.data;
  return (
    <div className="indicateurs">
      <Indicateur libelle="Solde du relevé" valeur={formaterMontant(e.soldeDuReleve)} detail={e.devise} />
      <Indicateur libelle="Solde comptable" valeur={formaterMontant(e.soldeComptable)} detail="pièces validées" />
      <Indicateur libelle="Écart" valeur={formaterMontant(e.ecart)} />
      <Indicateur
        libelle="Écart inexpliqué"
        valeur={formaterMontant(e.ecartInexplique)}
        detail={e.estExplique ? "expliqué par les suspens" : "à rechercher"}
      />
    </div>
  );
}

/* ------------------------------------------------------------------------ */

function Operations({ dossier, compte }: { dossier: string; compte: CompteBancaireVue }) {
  const { peut } = useSession();
  const cache = useQueryClient();
  const [statut, setStatut] = useState<StatutDOperation | "">("A_TRAITER");
  const [retour, setRetour] = useState<{ type: "succes" | "erreur"; texte: string } | null>(null);
  const [piece, setPiece] = useState<string | null>(null);

  const operations = useQuery({
    queryKey: ["operations-bancaires", compte.id, statut],
    queryFn: () => banqueApi.operations(compte.id, statut || undefined),
  });

  const rafraichir = () => {
    for (const cle of ["operations-bancaires", "rapprochement", "pieces", "grand-livre", "balance", "echeancier", "lettrage"]) {
      void cache.invalidateQueries({ queryKey: [cle] });
    }
  };
  const action = useMutation({
    mutationFn: (faire: () => Promise<unknown>) => faire(),
    onSuccess: rafraichir,
    onError: (echec) => setRetour({ type: "erreur", texte: message(echec) }),
    onMutate: () => setRetour(null),
  });
  const application = useMutation({
    mutationFn: () => banqueApi.appliquer(compte.id),
    onSuccess: ({ traitees }) => {
      rafraichir();
      setRetour({ type: "succes", texte: `${traitees} opération(s) traitée(s) selon leur unique proposition.` });
    },
    onError: (echec) => setRetour({ type: "erreur", texte: message(echec) }),
  });

  function imputer(operation: OperationVue) {
    const compteSaisi = window.prompt("Compte de contrepartie :", operation.montant > 0 ? "3421" : "613");
    if (compteSaisi == null || compteSaisi.trim() === "") return;
    const tiers = window.prompt("Tiers (si le compte est collectif) :", "") || null;
    const codeDeTva = window.prompt("Code de TVA si le montant est toutes taxes comprises (vide sinon) :", "") || null;
    action.mutate(() => banqueApi.comptabiliser(operation.id, { compte: compteSaisi.trim(), tiers, libelle: null, codeDeTva }));
  }

  return (
    <>
      <ImportDeReleve dossier={dossier} compte={compte} surImport={rafraichir} />

      {retour != null && <Message type={retour.type}>{retour.texte}</Message>}

      <div className="filtres-plan">
        <select value={statut} onChange={(e) => setStatut(e.target.value as StatutDOperation | "")} aria-label="Statut">
          <option value="A_TRAITER">À traiter</option>
          <option value="COMPTABILISEE">Comptabilisées</option>
          <option value="RAPPROCHEE">Rapprochées</option>
          <option value="IGNOREE">Ignorées</option>
          <option value="">Toutes</option>
        </select>
        {statut === "A_TRAITER" && peut("VALIDER_ECRITURE") && (
          <button type="button" className="tertiaire" disabled={application.isPending} onClick={() => application.mutate()}>
            Appliquer les propositions sans ambiguïté
          </button>
        )}
        {operations.data != null && <span className="decompte">{formaterNombre(operations.data.length)} opération(s)</span>}
      </div>

      {operations.isPending && <Chargement quoi="les opérations" />}
      {operations.data != null && operations.data.length === 0 && <Vide>Aucune opération dans cette vue.</Vide>}

      {operations.data != null && operations.data.length > 0 && (
        <div className="tableau-conteneur">
          <table className="operations-bancaires">
            <thead>
              <tr>
                <th>Date</th>
                <th>Libellé</th>
                <th className="nombre">Décaissement</th>
                <th className="nombre">Encaissement</th>
                <th>Traitement</th>
              </tr>
            </thead>
            <tbody>
              {operations.data.map((operation) => (
                <tr key={operation.id}>
                  <td>{formaterDate(operation.date)}</td>
                  <td>
                    {operation.libelle}
                    {operation.reference != null && <span className="discret-texte"> · {operation.reference}</span>}
                  </td>
                  <td className="nombre">{operation.montant < 0 ? formaterMontant(-operation.montant) : ""}</td>
                  <td className="nombre">{operation.montant > 0 ? formaterMontant(operation.montant) : ""}</td>
                  <td>
                    {operation.statut !== "A_TRAITER" ? (
                      <div className="jetons">
                        <Etiquette variante={operation.statut === "IGNOREE" ? "neutre" : "succes"}>{operation.statutLibelle}</Etiquette>
                        {operation.note != null && <span className="discret-texte">{operation.note}</span>}
                        {operation.piece != null && (
                          <button type="button" className="tertiaire" onClick={() => setPiece(operation.piece)}>
                            Pièce
                          </button>
                        )}
                        {(operation.statut === "RAPPROCHEE" || operation.statut === "IGNOREE") && (
                          <button type="button" className="tertiaire" onClick={() => action.mutate(() => banqueApi.rouvrir(operation.id))}>
                            Rouvrir
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="propositions-bancaires">
                        {operation.propositions.map((p, index) => (
                          <button
                            key={index}
                            type="button"
                            className={index === 0 ? "principal" : "tertiaire"}
                            title={p.motif}
                            disabled={action.isPending}
                            onClick={() =>
                              action.mutate(() =>
                                p.type === "POINTAGE"
                                  ? banqueApi.pointer(operation.id, p.ecriture!.piece, p.ecriture!.rang)
                                  : p.type === "REGLEMENT"
                                    ? banqueApi.comptabiliser(operation.id, { piece: p.poste!.piece, rang: p.poste!.rang })
                                    : banqueApi.comptabiliser(operation.id, { regle: p.regle! }),
                              )
                            }
                          >
                            {p.type === "POINTAGE" ? "Pointer" : p.type === "REGLEMENT" ? "Régler" : "Appliquer la règle"}
                            <span className="motif-proposition">{p.motif}</span>
                          </button>
                        ))}
                        <button type="button" className="tertiaire" onClick={() => imputer(operation)}>
                          Imputer…
                        </button>
                        <button
                          type="button"
                          className="tertiaire"
                          onClick={() => {
                            const motif = window.prompt("Motif :", "");
                            if (motif != null && motif.trim() !== "") action.mutate(() => banqueApi.ignorer(operation.id, motif.trim()));
                          }}
                        >
                          Ignorer…
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {piece != null && (
        <Tiroir titre="Pièce" surTitre={`${compte.compte} — ${compte.libelle}`} surFermeture={() => setPiece(null)}>
          <FicheDePiece piece={piece} dossier={dossier} compteEnEvidence={compte.compte} />
        </Tiroir>
      )}
    </>
  );
}

/* ------------------------------------------------------------------------ */

function ImportDeReleve({ dossier, compte, surImport }: { dossier: string; compte: CompteBancaireVue; surImport: () => void }) {
  const options = useQuery({ queryKey: ["banque-options"], queryFn: banqueApi.options });
  const [ouvert, setOuvert] = useState(false);
  const [fichier, setFichier] = useState<{ nom: string; contenu: string } | null>(null);
  const [apercu, setApercu] = useState<ApercuVue | null>(null);
  const [affectation, setAffectation] = useState<AffectationDeReleve>({});
  const [formatDeDate, setFormatDeDate] = useState(compte.correspondance?.formatDeDate ?? "JJ_MM_AAAA");
  const [avecEntete, setAvecEntete] = useState(compte.correspondance?.avecEntete ?? true);
  const [retour, setRetour] = useState<{ type: "succes" | "erreur" | "avertissement"; texte: string } | null>(null);

  async function lire(choisi: File, encodage: string) {
    setRetour(null);
    const contenu = new TextDecoder(encodage).decode(await choisi.arrayBuffer());
    setFichier({ nom: choisi.name, contenu });
    try {
      const lu = await importApi.apercu(dossier, contenu, null);
      setApercu(lu);
      setAffectation(
        compte.correspondance != null ? affectationDuReleve(compte.correspondance.colonnes) : proposerAffectationDeReleve(lu.lignes[0] ?? []),
      );
    } catch (echec) {
      setRetour({ type: "erreur", texte: message(echec) });
    }
  }

  const importation = useMutation({
    mutationFn: () =>
      banqueApi.importer(compte.id, {
        contenu: fichier!.contenu,
        separateur: null,
        correspondance: { colonnes: colonnesDuReleve(affectation), formatDeDate, avecEntete },
        fichier: fichier!.nom,
      }),
    onSuccess: (rapport) => {
      surImport();
      setApercu(null);
      setFichier(null);
      setOuvert(false);
      setRetour({
        type: rapport.rejets.length > 0 ? "avertissement" : "succes",
        texte:
          `${rapport.importees} opération(s) importée(s), ${rapport.doublons} déjà connue(s) écartée(s)` +
          (rapport.rejets.length > 0 ? `, ${rapport.rejets.length} ligne(s) rejetée(s) : ${rapport.rejets.map((r) => `ligne ${r.ligne} — ${r.motif}`).join(" ; ")}` : "") +
          ".",
      });
    },
    onError: (echec) => setRetour({ type: "erreur", texte: message(echec) }),
  });

  const aCorriger = manquesDuReleve(affectation);

  return (
    <>
      {retour != null && <Message type={retour.type}>{retour.texte}</Message>}
      {!ouvert ? (
        <p>
          <button type="button" className="tertiaire" onClick={() => setOuvert(true)}>
            Importer un relevé…
          </button>
        </p>
      ) : (
        <div className="carte">
          <h3>Importer un relevé CSV</h3>
          <div className="champs-en-ligne">
            <label className="champ">
              Fichier
              <input
                type="file"
                accept=".csv,.txt,text/csv"
                onChange={(e) => {
                  const choisi = e.target.files?.[0];
                  if (choisi != null) void lire(choisi, "utf-8");
                }}
                id="fichier-releve"
              />
            </label>
            <label className="champ">
              Format des dates
              <select value={formatDeDate} onChange={(e) => setFormatDeDate(e.target.value)}>
                {(options.data?.formatsDeDate ?? []).map((f) => (
                  <option key={f.code} value={f.code}>
                    {f.libelle}
                  </option>
                ))}
              </select>
            </label>
            <label className="discret-texte">
              <input type="checkbox" checked={avecEntete} onChange={(e) => setAvecEntete(e.target.checked)} /> Ligne d'en-têtes
            </label>
            <button type="button" className="tertiaire" onClick={() => setOuvert(false)}>
              Fermer
            </button>
          </div>
          {apercu != null && (
            <>
              <div className="tableau-conteneur apercu-import">
                <table>
                  <thead>
                    <tr>
                      {Array.from({ length: apercu.nombreDeColonnes }, (_, rang) => (
                        <th key={rang}>
                          <div className="discret-texte">{lettreDeColonne(rang)}</div>
                          <select
                            value={affectation[rang] ?? ""}
                            className={affectation[rang] != null ? "affectee" : undefined}
                            onChange={(e) => {
                              const champ = (e.target.value || undefined) as ChampDeReleve | undefined;
                              setAffectation((courante) => {
                                const suivante: AffectationDeReleve = {};
                                for (const [r, c] of Object.entries(courante)) if (c != null && c !== champ) suivante[Number(r)] = c;
                                suivante[rang] = champ;
                                return suivante;
                              });
                            }}
                          >
                            <option value="">— ignorée —</option>
                            {(options.data?.champs ?? []).map((c) => (
                              <option key={c.code} value={c.code}>
                                {c.libelle}
                              </option>
                            ))}
                          </select>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {apercu.lignes.slice(0, 6).map((ligne, index) => (
                      <tr key={index} className={avecEntete && index === 0 ? "entete-fichier" : undefined}>
                        {Array.from({ length: apercu.nombreDeColonnes }, (_, rang) => (
                          <td key={rang}>{ligne[rang] ?? ""}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {aCorriger.length > 0 ? (
                <p className="discret-texte">Reste à désigner : {aCorriger.join(" ; ")}.</p>
              ) : (
                <button type="button" className="principal" disabled={importation.isPending} onClick={() => importation.mutate()}>
                  Importer {formaterNombre(apercu.nombreDeLignes - (avecEntete ? 1 : 0))} ligne(s)
                </button>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------------ */

const REGLE_VIDE: SaisieDeRegle = {
  nom: "",
  priorite: 100,
  active: true,
  libelleContient: "",
  sens: "DECAISSEMENT",
  montantMinimal: null,
  montantMaximal: null,
  compteDeContrepartie: "",
  tiers: null,
  codeDeTva: null,
  libelleDEcriture: null,
};

function Regles({ dossier }: { dossier: string }) {
  const cache = useQueryClient();
  const regles = useQuery({ queryKey: ["regles-bancaires", dossier], queryFn: () => banqueApi.regles(dossier) });
  const [saisie, setSaisie] = useState<SaisieDeRegle>(REGLE_VIDE);
  const [simulation, setSimulation] = useState<OperationVue[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const nettoyee = (): SaisieDeRegle => ({
    ...saisie,
    libelleContient: saisie.libelleContient || null,
    tiers: saisie.tiers || null,
    codeDeTva: saisie.codeDeTva || null,
    libelleDEcriture: saisie.libelleDEcriture || null,
  });
  const simuler = useMutation({
    mutationFn: () => banqueApi.simulerRegle(dossier, nettoyee()),
    onMutate: () => setErreur(null),
    onSuccess: setSimulation,
    onError: (echec) => setErreur(message(echec)),
  });
  const creation = useMutation({
    mutationFn: () => banqueApi.creerRegle(dossier, nettoyee()),
    onMutate: () => setErreur(null),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ["regles-bancaires", dossier] });
      void cache.invalidateQueries({ queryKey: ["operations-bancaires"] });
      setSaisie(REGLE_VIDE);
      setSimulation(null);
    },
    onError: (echec) => setErreur(message(echec)),
  });
  const suppression = useMutation({
    mutationFn: (id: string) => banqueApi.supprimerRegle(id),
    onSuccess: () => void cache.invalidateQueries({ queryKey: ["regles-bancaires", dossier] }),
  });

  const texte = (nom: "nom" | "libelleContient" | "compteDeContrepartie" | "tiers" | "codeDeTva" | "libelleDEcriture", libelle: string, placeholder = "") => (
    <label className="champ">
      {libelle}
      <input
        value={saisie[nom] ?? ""}
        placeholder={placeholder}
        onChange={(e) => {
          setSimulation(null);
          setSaisie({ ...saisie, [nom]: e.target.value });
        }}
      />
    </label>
  );
  const nombre = (nom: "montantMinimal" | "montantMaximal" | "priorite", libelle: string) => (
    <label className="champ">
      {libelle}
      <input
        size={8}
        inputMode="decimal"
        value={saisie[nom] ?? ""}
        onChange={(e) => {
          setSimulation(null);
          const valeur = e.target.value.replace(",", ".").trim();
          setSaisie({ ...saisie, [nom]: valeur === "" ? null : Number(valeur) });
        }}
      />
    </label>
  );

  return (
    <>
      <div className="carte">
        <h3>Nouvelle règle</h3>
        {erreur != null && <Message type="erreur">{erreur}</Message>}
        <div className="champs-en-ligne">
          {texte("nom", "Nom", "Frais bancaires")}
          {texte("libelleContient", "Libellé contenant", "frais ; tenue")}
          <label className="champ">
            Sens
            <select
              value={saisie.sens ?? ""}
              onChange={(e) => setSaisie({ ...saisie, sens: (e.target.value || null) as SaisieDeRegle["sens"] })}
            >
              <option value="">Les deux</option>
              <option value="DECAISSEMENT">Décaissement</option>
              <option value="ENCAISSEMENT">Encaissement</option>
            </select>
          </label>
          {nombre("montantMinimal", "Montant de")}
          {nombre("montantMaximal", "à")}
          {texte("compteDeContrepartie", "Compte", "613")}
          {texte("tiers", "Tiers")}
          {texte("codeDeTva", "Code TVA (TTC)", "REDUIT_10")}
          {nombre("priorite", "Priorité")}
        </div>
        <div className="barre-actions">
          <button type="button" className="tertiaire" disabled={simuler.isPending} onClick={() => simuler.mutate()}>
            Simuler
          </button>
          <button type="button" className="principal" disabled={creation.isPending} onClick={() => creation.mutate()}>
            Enregistrer la règle
          </button>
          {simulation != null && (
            <span className="discret-texte">
              {simulation.length === 0
                ? "Aucune opération à traiter ne serait reconnue."
                : `${simulation.length} opération(s) à traiter reconnue(s) : ${simulation
                    .slice(0, 4)
                    .map((o) => `${formaterDate(o.date)} ${o.libelle} (${formaterMontant(o.montant)})`)
                    .join(" ; ")}${simulation.length > 4 ? "…" : ""}`}
            </span>
          )}
        </div>
      </div>

      {regles.data != null && regles.data.length === 0 && <Vide>Aucune règle : les propositions ne viennent que des écritures et des factures ouvertes.</Vide>}
      {regles.data != null && regles.data.length > 0 && (
        <div className="tableau-conteneur">
          <table>
            <thead>
              <tr>
                <th className="nombre">Priorité</th>
                <th>Règle</th>
                <th>Reconnaît</th>
                <th>Impute</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {regles.data.map((r) => (
                <tr key={r.id}>
                  <td className="nombre">{r.priorite}</td>
                  <td>
                    {r.nom} {!r.active && <Etiquette variante="neutre">Inactive</Etiquette>}
                  </td>
                  <td className="discret-texte">{r.explication}</td>
                  <td>
                    <span className="numero-compte">{r.compteDeContrepartie}</span>
                    {r.tiers != null && ` · ${r.tiers}`}
                    {r.codeDeTva != null && <span className="discret-texte"> · TVA {r.codeDeTva} incluse</span>}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="tertiaire"
                      onClick={() => window.confirm(`Supprimer la règle « ${r.nom} » ?`) && suppression.mutate(r.id)}
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------------ */

function Rapprochement({ dossier, compte }: { dossier: string; compte: CompteBancaireVue }) {
  const [date, setDate] = useState(aujourdHui());
  const [piece, setPiece] = useState<string | null>(null);
  const etat = useQuery({ queryKey: ["rapprochement", compte.id, date], queryFn: () => banqueApi.rapprochement(compte.id, date) });

  return (
    <>
      <div className="filtres-plan">
        <label className="champ">
          État arrêté au
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
      </div>
      {etat.error != null && <Message type="erreur">{message(etat.error)}</Message>}
      {etat.isPending && <Chargement quoi="l'état de rapprochement" />}
      {etat.data != null && (
        <div className="carte etat-de-rapprochement">
          <table>
            <tbody>
              <tr>
                <td>Solde du relevé au {formaterDate(etat.data.date)}</td>
                <td className="nombre">{formaterMontant(etat.data.soldeDuReleve)}</td>
              </tr>
              <tr>
                <td>− Opérations du relevé non comptabilisées</td>
                <td className="nombre">{formaterMontant(etat.data.totalNonComptabilise)}</td>
              </tr>
              <tr>
                <td>+ Écritures non encore passées en banque</td>
                <td className="nombre">{formaterMontant(etat.data.totalNonPointe)}</td>
              </tr>
              <tr className="total">
                <td>= Solde comptable rapproché</td>
                <td className="nombre">
                  {formaterMontant(etat.data.soldeDuReleve - etat.data.totalNonComptabilise + etat.data.totalNonPointe)}
                </td>
              </tr>
              <tr>
                <td>Solde comptable du compte {compte.compte}</td>
                <td className="nombre">{formaterMontant(etat.data.soldeComptable)}</td>
              </tr>
              <tr className="total">
                <td>
                  Écart inexpliqué{" "}
                  <Etiquette variante={etat.data.estExplique ? "succes" : "bloquant"}>
                    {etat.data.estExplique ? "Rapproché" : "À rechercher"}
                  </Etiquette>
                </td>
                <td className="nombre">{formaterMontant(etat.data.ecartInexplique)}</td>
              </tr>
            </tbody>
          </table>

          <h3>Opérations du relevé non comptabilisées</h3>
          {etat.data.operationsNonComptabilisees.length === 0 ? (
            <p className="discret-texte">Aucune.</p>
          ) : (
            <table>
              <tbody>
                {etat.data.operationsNonComptabilisees.map((o) => (
                  <tr key={o.id}>
                    <td>{formaterDate(o.date)}</td>
                    <td>
                      {o.libelle}
                      {o.statut === "IGNOREE" && <span className="discret-texte"> · ignorée : {o.note}</span>}
                    </td>
                    <td className="nombre">{formaterMontant(o.montant)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3>Écritures non encore passées en banque</h3>
          {etat.data.ecrituresNonPointees.length === 0 ? (
            <p className="discret-texte">Aucune.</p>
          ) : (
            <table>
              <tbody>
                {etat.data.ecrituresNonPointees.map((e) => (
                  <tr key={`${e.piece}-${e.rang}`} className="cliquable" onClick={() => setPiece(e.piece)}>
                    <td>{formaterDate(e.date)}</td>
                    <td>
                      {e.journal} · {e.libelle}
                      {e.reference != null && <span className="discret-texte"> · {e.reference}</span>}
                    </td>
                    <td className="nombre">{formaterMontant(e.montant)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {piece != null && (
        <Tiroir titre="Pièce" surFermeture={() => setPiece(null)}>
          <FicheDePiece piece={piece} dossier={dossier} compteEnEvidence={compte.compte} />
        </Tiroir>
      )}
    </>
  );
}
